import { z } from 'zod';
import { zMoney } from './primitives.js';

/**
 * OR-01 — which of the retailer's OWN persisted cart lines to submit right
 * now. Selecting a line is not supplying its price or quantity — both still
 * come from the cart record itself (`order.controller.ts`'s `submit()`),
 * never the client, so a request can narrow what gets submitted but never
 * inflate it. Omitted (or an absent body) means "the whole cart", so the
 * original no-body call keeps working unchanged.
 */
/**
 * CR-54 / D102 — the commercial basis of an order. One order is one basis; there is no mixed cart.
 * `requested_basis` (what the retailer asked for at R-05) and `commercial_basis` (what Miraki agreed
 * on A-15) share this vocabulary. Only the second is ever read by the budget, receipt or invoice.
 */
export const commercialBases = ['CONSIGNMENT', 'OUTRIGHT'] as const;
export type CommercialBasis = (typeof commercialBases)[number];
export const zCommercialBasis = z.enum(commercialBases);

export const zSubmitOrder = z.object({
  /** D102(a) — a REQUEST only. Defaults to CONSIGNMENT; it never sets the effective basis. */
  requestedBasis: zCommercialBasis.optional(),
  variantCodes: z.array(z.string().min(1).max(64)).min(1, 'Select at least one line to submit').optional(),
});

/**
 * OR-03 — revising lines during review: the agreed price, quantity, size and
 * remark. Each field is optional — only what is sent changes.
 *
 * The SUBMITTED price is not in this schema at all, so a client cannot rewrite
 * what the retailer originally asked for — which is the whole value of keeping
 * both (OR-04b).
 */
export const zReprice = z.object({
  lines: z.array(z.object({
    lineNumber: z.number().int().positive(),
    agreedUnitPrice: zMoney.refine((m) => !m.isNegative(), 'cannot be negative').optional(),
    quantity: z.number().int().positive().max(100_000).optional(),
    /** Null or blank clears it. Same limits as the cart line it came from. */
    size: z.string().trim().max(32).nullable().optional(),
    remark: z.string().trim().max(1000).nullable().optional(),
  }).refine(
    (l) => l.agreedUnitPrice !== undefined || l.quantity !== undefined || l.size !== undefined || l.remark !== undefined,
    'Nothing to change on this line',
  )).min(1),
});

/** The retailer's feedback on an offer — sends the order back to review. */
export const zRequestRevision = z.object({ feedback: z.string().trim().min(3).max(4000) });

/** The retailer's decline — the reason is optional. */
export const zDeclineOrder = z.object({ reason: z.string().trim().max(1000).optional() });

/** Transitions the API accepts by name, so a typo is a 400 rather than a 409. */
export const zOrderAction = z.object({
  action: z.enum([
    'confirm', 'beginReview', 'sendForAcceptance', 'accept', 'decline', 'expire',
    'requireAdvance', 'beginManufacture', 'markReady', 'ship', 'markDelivered',
    'cancelByRetailer', 'cancelByMiraki', 'reject',
  ]),
  reason: z.string().max(1000).optional(),
  /** CR-54 / D102(a) — the EFFECTIVE basis, honoured on `confirm` only and ignored for every other action. */
  basis: zCommercialBasis.optional(),
});

/** OR-07 — cancelling and rejecting carry a reason someone can read later. */
export const zCloseOrder = z.object({ reason: z.string().min(3).max(1000) });

export const zRaiseQuery = z.object({
  subject: z.string().min(3).max(200),
  body: z.string().min(3).max(4000),
});
export const zAnswerQuery = z.object({ body: z.string().min(3).max(4000) });

/** A message in an order's conversation (OR-14), from either side. A chat
 *  line can be "ok" or "yes", so the floor is one character, not three. */
export const zOrderMessage = z.object({ body: z.string().trim().min(1).max(4000) });

export type SubmitOrder = z.infer<typeof zSubmitOrder>;
export type Reprice = z.infer<typeof zReprice>;

/**
 * A retailer's request to change the quantity and/or size of lines on an order that is not yet in
 * manufacturing. Nothing changes until Miraki approves it; the order stays as confirmed until then.
 * Only what differs is sent. Same bounds as `zReprice`, which is how Miraki edits the same fields.
 */
export const zOrderChangeRequest = z.object({
  lines: z.array(z.object({
    lineId: z.string().uuid(),
    quantity: z.number().int().positive().max(100_000).optional(),
    /** Blank clears the size. */
    size: z.string().trim().max(32).nullable().optional(),
  }).refine((l) => l.quantity !== undefined || l.size !== undefined, 'Nothing to change on this line'))
    .min(1).max(200),
});
export type OrderChangeRequest = z.infer<typeof zOrderChangeRequest>;

/** Miraki's decision on a change request. A rejection says why; the retailer is shown the reason. */
export const zOrderChangeDecision = z.object({
  decision: z.enum(['approve', 'reject']),
  reason: z.string().trim().max(300).optional(),
}).refine((d) => d.decision === 'approve' || (d.reason ?? '').length >= 3, {
  message: 'Say why the change is rejected', path: ['reason'],
});

/** A retailer's request to change an address. Applied only when Miraki approves it. */
export const zAddressChangeRequest = z.object({
  /** The address being changed; absent to add the first address of that kind. */
  addressId: z.string().uuid().optional(),
  kind: z.enum(['REGISTERED', 'BILLING', 'SHIPPING']),
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).nullish(),
  city: z.string().trim().min(1).max(100),
  postalCode: z.string().trim().max(20).nullish(),
  countryCode: z.string().trim().length(2).nullish(),
  contactName: z.string().trim().max(120).nullish(),
  contactPhone: z.string().trim().max(40).nullish(),
});
export type AddressChangeRequest = z.infer<typeof zAddressChangeRequest>;

export const zAddressChangeDecision = zOrderChangeDecision;

/** The statuses staff may move a batch of orders to from the queue. Shipped and Delivered are absent on purpose:
 *  those follow a shipment and the retailer's receipt, never a bulk edit. */
export const BULK_ORDER_STATUSES = [
  'UNDER_REVIEW', 'APPROVED', 'IN_MANUFACTURING', 'READY', 'REJECTED', 'CANCELLED',
] as const;
export type BulkOrderStatus = (typeof BULK_ORDER_STATUSES)[number];

/** A-14 — move several orders to one status. Each order is checked on its own against the lifecycle. */
export const zBulkOrderStatus = z.object({
  orderIds: z.array(z.string().uuid()).min(1).max(100),
  status: z.enum(BULK_ORDER_STATUSES),
  reason: z.string().max(1000).optional(),
}).refine((b) => !['REJECTED', 'CANCELLED'].includes(b.status) || (b.reason?.trim().length ?? 0) >= 3, {
  message: 'A reason of at least 3 characters is required to reject or cancel orders.', path: ['reason'],
});
export type BulkOrderStatusRequest = z.infer<typeof zBulkOrderStatus>;
